import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Filter, X } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=project`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const toggleCategory = (categoryName) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryName) ? prev.filter((c) => c !== categoryName) : [...prev, categoryName]
    );
  };

  const clearFilters = () => {
    setFilter('all');
    setSelectedCategories([]);
    setSearchQuery('');
  };

  // Filtrado avanzado con búsqueda en tiempo real
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      // Filtro por tipo (real/ficticio/all)
      if (filter !== 'all' && project.type !== filter) return false;

      // Filtro por categorías seleccionadas
      if (selectedCategories.length > 0 && !selectedCategories.includes(project.category)) return false;

      // Búsqueda en tiempo real (título, descripción, categoría, cliente)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = project.title.toLowerCase().includes(query);
        const matchesDescription = project.description.toLowerCase().includes(query);
        const matchesCategory = project.category.toLowerCase().includes(query);
        const matchesClient = project.client?.toLowerCase().includes(query) || false;

        if (!matchesTitle && !matchesDescription && !matchesCategory && !matchesClient) return false;
      }

      return true;
    });
  }, [projects, filter, selectedCategories, searchQuery]);

  const activeFiltersCount = (filter !== 'all' ? 1 : 0) + selectedCategories.length;

  return (
    <>
      <SEO
        title="Proyectos | Portfolio"
        description="Explora mi portfolio de proyectos de diseño gráfico y fotografía. Trabajos reales y conceptuales."
      />

      <div className="pt-32 pb-24">
        {/* Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-12" data-testid="projects-header">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4">Proyectos</p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8" data-testid="projects-title">
                Portfolio Completo
              </h1>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-6"
            >
              <div className="relative max-w-2xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink/40" />
                <input
                  type="text"
                  placeholder="Buscar proyectos por título, descripción, categoría o cliente..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full pl-12 pr-12 py-4 border border-ink/20 focus:border-editorial focus:outline-none text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-ink/40 hover:text-editorial transition-colors"
                    data-testid="clear-search"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* Filters */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              data-testid="projects-filter"
            >
              {/* Type Filter + Toggle Advanced Filters */}
              <div className="flex flex-wrap gap-4 items-center mb-4">
                <button
                  onClick={() => setFilter('all')}
                  data-testid="filter-all"
                  className={`px-6 py-3 text-sm font-sans tracking-widest uppercase transition-all duration-200 ${
                    filter === 'all'
                      ? 'bg-ink text-paper'
                      : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                  }`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setFilter('real')}
                  data-testid="filter-real"
                  className={`px-6 py-3 text-sm font-sans tracking-widest uppercase transition-all duration-200 ${
                    filter === 'real'
                      ? 'bg-ink text-paper'
                      : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                  }`}
                >
                  Proyectos Reales
                </button>
                <button
                  onClick={() => setFilter('ficticio')}
                  data-testid="filter-ficticio"
                  className={`px-6 py-3 text-sm font-sans tracking-widest uppercase transition-all duration-200 ${
                    filter === 'ficticio'
                      ? 'bg-ink text-paper'
                      : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                  }`}
                >
                  Proyectos Ficticios
                </button>

                {/* Toggle Categories */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="toggle-filters"
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-sans tracking-widest uppercase transition-all duration-200 ${
                    showFilters || selectedCategories.length > 0
                      ? 'bg-editorial text-paper'
                      : 'border border-ink text-ink hover:bg-ink hover:text-paper'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Categorías
                  {selectedCategories.length > 0 && (
                    <span className="bg-paper text-ink px-2 py-0.5 text-xs rounded-full">
                      {selectedCategories.length}
                    </span>
                  )}
                </button>

                {/* Clear All Filters */}
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    data-testid="clear-filters"
                    className="text-sm font-sans tracking-widest uppercase text-editorial hover:underline"
                  >
                    Limpiar filtros ({activeFiltersCount})
                  </button>
                )}
              </div>

              {/* Category Checkboxes */}
              {showFilters && categories.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border border-ink/20 p-6 mb-4"
                  data-testid="category-filters"
                >
                  <p className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-4">
                    Filtrar por Categoría:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {categories.map((category) => (
                      <label
                        key={category.id}
                        className={`flex items-center gap-2 px-4 py-2 border cursor-pointer transition-all ${
                          selectedCategories.includes(category.name)
                            ? 'border-editorial bg-editorial text-paper'
                            : 'border-ink/20 hover:border-ink'
                        }`}
                        data-testid={`category-${category.name.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.name)}
                          onChange={() => toggleCategory(category.name)}
                          className="hidden"
                        />
                        <span className="text-sm font-sans tracking-wide">{category.name}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="px-6 md:px-12 lg:px-24" data-testid="projects-grid">
          <div className="max-w-screen-2xl mx-auto">
            {/* Results Count */}
            {!loading && (
              <p className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-8">
                {filteredProjects.length} {filteredProjects.length === 1 ? 'proyecto' : 'proyectos'} encontrado
                {filteredProjects.length !== 1 ? 's' : ''}
              </p>
            )}

            {loading ? (
              <div className="text-center py-20">
                <p className="text-lg font-light text-ink/60">Cargando proyectos...</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-light text-ink/60 mb-4" data-testid="no-projects-message">
                  No se encontraron proyectos con los filtros seleccionados.
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm tracking-widest font-sans uppercase hover:bg-ink hover:text-paper transition-colors duration-200"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="group"
                    data-testid={`project-card-${index}`}
                  >
                    <Link to={`/projects/${project.slug}`}>
                      <div className="image-zoom-container editorial-border h-80 mb-4">
                        <img
                          src={project.images[0]}
                          alt={project.title}
                          className="w-full h-full object-cover image-zoom"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-xs font-sans tracking-widest uppercase text-ink/60">
                          {project.category}
                        </p>
                        {project.type === 'ficticio' && (
                          <span className="text-xs font-sans tracking-widest uppercase text-editorial">
                            Concepto
                          </span>
                        )}
                      </div>
                      <h3 className="text-2xl font-bold tracking-tight group-hover:text-editorial transition-colors mb-2">
                        {project.title}
                      </h3>
                      <p className="text-sm font-light text-ink/70">{project.year}</p>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Projects;